import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CommunityStats } from '@shared/types';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAsync } from '../lib/useAsync';
import { useToast } from '../lib/toast';
import { Banner, EmptyState, Field, LoadingPage, Stat } from '../components/ui';
import { AdminNav } from './admin/AdminNav';
import { CommunityFields, communityToForm, formToPayload } from './CommunityPage';
import { PageHeader } from '../components/PageHeader';

export function SuperAdminPage() {
  const { user, setUser, setCommunity } = useAuth();
  const { notify, fail } = useToast();
  const list = useAsync(async () => (await api.adminCommunities()).communities, []);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(communityToForm(null));
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const [joiningId, setJoiningId] = useState<string | null>(null);
  const [lastCode, setLastCode] = useState<string | null>(null);
  const [admin, setAdmin] = useState({ name: '', email: '', phone: '' });

  async function createCommunity(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const { community, user: nextUser, invitedEmail, emailed, mailError } =
        await api.adminCreateCommunity({
          ...formToPayload(form),
          adminName: admin.name,
          adminEmail: admin.email,
          adminPhone: admin.phone,
        });
      setUser(nextUser);
      if (nextUser.communityId === community.id) setCommunity(community);
      await list.reload();
      setForm(communityToForm(null));
      setAdmin({ name: '', email: '', phone: '' });
      setCreating(false);
      notify(
        emailed
          ? `${community.name} is ready. A board member invitation was sent to ${invitedEmail}.`
          : `${community.name} is ready, but the board invitation to ${invitedEmail} was not sent.${mailError ? ` ${mailError}` : ''}`,
        emailed ? 'success' : 'error',
      );
    } catch (error) {
      fail(error, 'The community was not created.');
    } finally {
      setBusy(false);
    }
  }

  async function joinCommunity(communityId: string, name: string) {
    setJoiningId(communityId);
    try {
      const { community, user: nextUser } = await api.adminJoinCommunity(communityId);
      setUser(nextUser);
      setCommunity(community);
      await list.reload();
      notify(`You're now a member of ${name}.`, 'success');
    } catch (error) {
      fail(error, 'You were not added to that community.');
    } finally {
      setJoiningId(null);
    }
  }

  async function manageCommunity(communityId: string) {
    try {
      const { community, user: nextUser } = await api.adminActInCommunity(communityId);
      setUser(nextUser);
      setCommunity(community);
      navigate('/board/residents');
    } catch (error) {
      fail(error, 'That community could not be opened.');
    }
  }

  async function inviteSuper() {
    try {
      const { code } = await api.adminSuperInvite({ note: 'Additional super admin' });
      setLastCode(code);
      notify(`Super admin invite ${code} created.`, 'success');
    } catch (error) {
      fail(error, 'The super admin invite was not created.');
    }
  }

  async function toggleCommunityStatus(id: string, name: string, currentlyDisabled: boolean) {
    if (currentlyDisabled) {
      try {
        await api.adminEnableCommunity(id);
        await list.reload();
        notify(`${name} is active again.`, 'success');
      } catch (error) {
        fail(error, 'The community was not reactivated.');
      }
      return;
    }
    const reason = window.prompt(`Disable ${name}? Optional reason (Cancel aborts):`);
    if (reason === null) return;
    if (!window.confirm(`Disable ${name}? Members will not be able to sign in until it is reactivated.`)) {
      return;
    }
    try {
      await api.adminDisableCommunity(id, reason.trim() || null);
      await list.reload();
      notify(`${name} is disabled.`, 'success');
    } catch (error) {
      fail(error, 'The community was not disabled.');
    }
  }

  if (list.loading && !list.data) return <LoadingPage label="Loading communities…" />;
  const communities = list.data ?? [];

  return (
    <div>
      <PageHeader
        title="Community Management"
        lede="Create and manage communities, invite the first board member, and review activity across every community on this portal."
      />
      <AdminNav />

      <div className="card">
        <div className="stat-grid">
          <Stat label="Communities" value={communities.length} />
          <Stat label="Residents" value={communities.reduce((sum, item) => sum + item.members, 0)} />
          <Stat label="Open issues" value={communities.reduce((sum, item) => sum + item.openIssues, 0)} />
          <Stat
            label="Open violations"
            value={communities.reduce((sum, item) => sum + item.openViolations, 0)}
          />
        </div>
      </div>

      {lastCode && (
        <Banner kind="ok">
          Share this invite code: <strong>{lastCode}</strong>
        </Banner>
      )}

      <div className="row" style={{ marginBottom: 14 }}>
        <button type="button" className="btn" onClick={() => setCreating((open) => !open)}>
          {creating ? 'Close form' : 'New community'}
        </button>
        <button type="button" className="btn s" onClick={() => void inviteSuper()}>
          Invite a super admin
        </button>
      </div>

      {creating && (
        <form className="card" onSubmit={createCommunity}>
          <h3>Create a community</h3>
          <CommunityFields value={form} onChange={setForm} />
          <h3 style={{ marginTop: 18 }}>Board member</h3>
          <p className="mut">
            We email this person the board member invitation so they can accept and set up the
            community. They are invited as both a Board Member and an Admin.
          </p>
          <Field label="Board member name">
            <input
              value={admin.name}
              onChange={(event) => setAdmin((current) => ({ ...current, name: event.target.value }))}
              required
              placeholder="Dana Whitfield"
            />
          </Field>
          <Field label="Board member email">
            <input
              type="email"
              value={admin.email}
              onChange={(event) => setAdmin((current) => ({ ...current, email: event.target.value }))}
              required
              placeholder="dana@willowcreekhoa.org"
            />
          </Field>
          <Field label="Board member phone">
            <input
              type="tel"
              value={admin.phone}
              onChange={(event) => setAdmin((current) => ({ ...current, phone: event.target.value }))}
              required
              placeholder="512-555-0142"
            />
          </Field>
          <div className="btn-row">
            <button type="submit" className="btn" disabled={busy}>
              {busy ? 'Creating…' : 'Create community and invite board member'}
            </button>
          </div>
        </form>
      )}

      {communities.length === 0 ? (
        <EmptyState title="No communities yet." hint="Create the first HOA above and invite its admin." />
      ) : (
        communities.map((item) => (
          <CommunityCard
            key={item.community.id}
            stats={item}
            isCurrent={!user?.actingCommunityId && user?.communityId === item.community.id}
            onManage={manageCommunity}
            joining={joiningId === item.community.id}
            onJoin={joinCommunity}
            onToggleStatus={toggleCommunityStatus}
            onChanged={() => list.reload()}
          />
        ))
      )}
    </div>
  );
}

function CommunityCard({
  stats,
  isCurrent,
  onManage,
  joining,
  onJoin,
  onToggleStatus,
  onChanged,
}: {
  stats: CommunityStats;
  isCurrent: boolean;
  onManage: (id: string) => void;
  joining: boolean;
  onJoin: (id: string, name: string) => void;
  onToggleStatus: (id: string, name: string, disabled: boolean) => void;
  onChanged: () => Promise<void>;
}) {
  const { notify, fail } = useToast();
  const { community } = stats;
  const disabled = community.status === 'disabled';
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => communityToForm(community));
  const [board, setBoard] = useState({
    name: community.boardContactName ?? '',
    email: community.boardEmail ?? '',
    phone: community.boardContactPhone ?? '',
  });
  const [busy, setBusy] = useState(false);
  const detail = useAsync(async () => {
    if (!open) return null;
    return api.adminCommunityDetail(community.id);
  }, [open, community.id]);

  const invite = detail.data?.boardInvite;
  const contact = detail.data?.primaryContact;

  async function saveCommunity(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await api.adminUpdateCommunity(community.id, formToPayload(form));
      setEditing(false);
      await onChanged();
      notify('Community details saved.', 'success');
    } catch (error) {
      fail(error, 'Community details were not saved.');
    } finally {
      setBusy(false);
    }
  }

  async function saveBoard(resend: boolean) {
    setBusy(true);
    try {
      const result = await api.adminSaveBoardMember(community.id, { ...board, resend });
      await Promise.all([onChanged(), detail.reload()]);
      notify(
        !resend
          ? 'Board member details saved.'
          : result.emailed
            ? `Board invitation sent${result.code ? ` (${result.code})` : ''}.`
            : `Board member saved, but the email was not sent.${result.mailError ? ` ${result.mailError}` : ''}`,
        !resend || result.emailed ? 'success' : 'error',
      );
    } catch (error) {
      fail(error, 'Board member details were not saved.');
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setBusy(true);
    try {
      const result = await api.adminResendBoardInvite(community.id);
      await detail.reload();
      notify(
        result.emailed
          ? `Board invitation resent (${result.code}).`
          : `The invitation was not emailed.${result.mailError ? ` ${result.mailError}` : ''}`,
        result.emailed ? 'success' : 'error',
      );
    } catch (error) {
      fail(error, 'The board invitation was not resent.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h3>
            {community.name} {disabled && <span className="tag bad">Disabled</span>}
          </h3>
          <div className="mut">{community.address || 'Address not set yet'}</div>
        </div>
        <div className="btn-row" style={{ margin: 0 }}>
          <button type="button" className="btn tiny" onClick={() => onManage(community.id)}>
            Manage
          </button>
          <button type="button" className="btn s tiny" onClick={() => setOpen((value) => !value)}>
            {open ? 'Hide' : 'View'}
          </button>
          <button
            type="button"
            className="btn s tiny"
            onClick={() => {
              setForm(communityToForm(community));
              setEditing((value) => !value);
              setOpen(true);
            }}
          >
            {editing ? 'Close edit' : 'Edit'}
          </button>
          {isCurrent ? (
            <span className="mut">Your community</span>
          ) : (
            <button
              type="button"
              className="btn s tiny"
              disabled={joining || disabled}
              onClick={() => onJoin(community.id, community.name)}
            >
              {joining ? 'Joining…' : 'Join'}
            </button>
          )}
          <button
            type="button"
            className="btn s tiny"
            onClick={() => onToggleStatus(community.id, community.name, disabled)}
          >
            {disabled ? 'Reactivate' : 'Disable'}
          </button>
        </div>
      </div>
      <div className="stat-grid" style={{ marginTop: 12 }}>
        <Stat label="Active" value={stats.activeMembers ?? stats.members} />
        <Stat label="Invited" value={stats.invitedMembers ?? 0} />
        <Stat label="Tenants" value={stats.tenants} />
        <Stat label="Board" value={stats.boardMembers} />
        <Stat label="Open issues" value={stats.openIssues} />
        <Stat label="Violations" value={stats.openViolations} />
      </div>

      {editing && (
        <form onSubmit={saveCommunity} style={{ marginTop: 16 }}>
          <h4>Edit community</h4>
          <CommunityFields value={form} onChange={setForm} />
          <button type="submit" className="btn" disabled={busy}>
            {busy ? 'Saving…' : 'Save community'}
          </button>
        </form>
      )}

      {open && (
        <div style={{ marginTop: 16 }}>
          <h4>Board member</h4>
          {contact && (
            <p className="mut">
              Active primary contact: {contact.name} · {contact.email}
              {contact.phone ? ` · ${contact.phone}` : ''}
            </p>
          )}
          {invite && (
            <p className="mut">
              {invite.email
                ? `Invitation ${invite.code} is ${invite.status} for ${invite.email}${invite.deliveryError ? ` — ${invite.deliveryError}` : ''}.`
                : `Invitation ${invite.code} is ${invite.status} and has no email address yet. Save and send to email this board member.`}
            </p>
          )}
          <Field label="Name">
            <input
              value={board.name}
              onChange={(event) => setBoard({ ...board, name: event.target.value })}
              required
            />
          </Field>
          <Field label="Email">
            <input
              type="email"
              value={board.email}
              onChange={(event) => setBoard({ ...board, email: event.target.value })}
              required
            />
          </Field>
          <Field label="Phone">
            <input
              type="tel"
              value={board.phone}
              onChange={(event) => setBoard({ ...board, phone: event.target.value })}
              required
            />
          </Field>
          <div className="btn-row">
            <button type="button" className="btn" disabled={busy} onClick={() => void saveBoard(true)}>
              Save and send board invitation
            </button>
            <button type="button" className="btn s" disabled={busy || !invite?.email} onClick={() => void resend()}>
              Resend invitation
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
